import { useCollectionServices } from '../../../contexts/CollectionSourceContext';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import BarChartOutlined from '@mui/icons-material/BarChartOutlined';
import { FitbitMetric, FitbitSummary } from '../../../services/fitbitCollectionDataService';
import DeviceMetricPicker from '../collection/metrics/DeviceMetricPicker';
import MetricVerification from '../collection/metrics/MetricVerification';
import DeviceMeasurementContext from '../collection/metrics/DeviceMeasurementContext';
import FitbitNumericChart from './FitbitNumericChart';
import FitbitStructuredChart from './FitbitStructuredChart';
import FitbitAvailabilitySummary from './FitbitAvailabilitySummary';
import { DateRange, datesInRange, validRange } from './dataAvailability';
import { readStoredHistory } from './storedHistory';
import { defaultField, fieldPresentation, metricPresentation } from '../collection/metrics/metricPresentation';
import { PlotChoice, PlotResult, readVisualizations } from './visualizationReads';

type AppliedSelection = { key: string; range: DateRange; choices: PlotChoice[]; run: number };
export default function FitbitVisualizationExplorer({ studyId, participantId, catalog, range, revision, onUseDates }: {
  studyId: string; participantId: string; catalog: FitbitMetric[]; range: DateRange;
  revision: number; onUseDates: (range: DateRange) => void;
}) {
  const { exportFitbitRecords, readFitbitData } = useCollectionServices();
  const [selected, setSelected] = useState<string[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [applied, setApplied] = useState<AppliedSelection | null>(null);
  const [results, setResults] = useState<Record<string, PlotResult>>({});
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<{ summary: FitbitSummary; range: DateRange; fullHistory: boolean } | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);
  const [exportError, setExportError] = useState('');
  const controller = useRef<AbortController | null>(null);
  const exportController = useRef<AbortController | null>(null);
  const historyController = useRef<AbortController | null>(null);
  const run = useRef(0);

  useEffect(() => {
    controller.current?.abort(); exportController.current?.abort(); historyController.current?.abort();
    setApplied(null); setResults({}); setLoading(false); setExportError('');
    setHistory(null); setHistoryError(false); setHistoryLoading(false);
    return () => { controller.current?.abort(); exportController.current?.abort(); historyController.current?.abort(); };
  }, [revision]);

  const choices = selected.flatMap(routeKey => {
    const metric = catalog.find(item => item.routeKey === routeKey);
    if (!metric) return [];
    const fieldPath = metric.fields.some(field => field.path === fields[routeKey]) ? fields[routeKey] : defaultField(metric);
    return [{ metric, fieldPath }];
  });
  const draftKey = JSON.stringify([range.startDate, range.endDate, choices.map(choice => [choice.metric.routeKey, choice.fieldPath])]);
  const changed = !!applied && applied.key !== draftKey;
  const chartDates = useMemo(() => applied ? datesInRange(applied.range) : [], [applied]);
  const toggleMetric = (routeKey: string) => setSelected(values => values.includes(routeKey) ? values.filter(value => value !== routeKey) : [...values, routeKey]);

  const loadAvailability = async () => {
    historyController.current?.abort();
    const nextController = new AbortController(); historyController.current = nextController;
    setHistoryLoading(true); setHistoryError(false);
    const now = new Date();
    try {
      const result = await readStoredHistory(studyId, participantId, nextController.signal, now, readFitbitData);
      if (!nextController.signal.aborted) setHistory({ summary: result.summary, fullHistory: !!result.requestedRange,
        range: result.requestedRange || { startDate: now.toISOString().slice(0, 10), endDate: now.toISOString().slice(0, 10) } });
    } catch { if (!nextController.signal.aborted) setHistoryError(true); }
    finally { if (!nextController.signal.aborted) setHistoryLoading(false); }
  };

  const visualize = () => {
    if (!validRange(range) || !choices.length) return;
    controller.current?.abort(); exportController.current?.abort();
    const nextController = new AbortController(); controller.current = nextController;
    const next = { key: draftKey, range: { ...range }, choices, run: ++run.current };
    setApplied(next); setResults({}); setLoading(true); setExportError('');
    void readVisualizations(studyId, participantId, next.range, next.choices, nextController.signal,
      result => setResults(current => ({ ...current, [result.metric.routeKey]: result })), readFitbitData)
      .finally(() => { if (!nextController.signal.aborted) setLoading(false); });
  };

  const download = async (routeKey: string) => {
    if (!applied) return;
    const nextController = new AbortController(); exportController.current = nextController;
    setExporting(routeKey); setExportError('');
    try {
      const blob = await exportFitbitRecords(studyId, participantId, { ...applied.range, routeKey }, () => {}, nextController.signal);
      if (nextController.signal.aborted) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = url;
      link.download = `fitbit-${participantId}-${routeKey}-${applied.range.startDate}-${applied.range.endDate}.ndjson`; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) { if (!nextController.signal.aborted) setExportError(error instanceof Error ? error.message : 'Export failed. Please retry.'); }
    finally { if (exportController.current === nextController) setExporting(null); }
  };

  return <>
    {history ? <FitbitAvailabilitySummary summary={history.summary} range={history.range} history={history.fullHistory} initiallyExpanded
      onUseDates={(dates, routeKey) => { setSelected(values => values.includes(routeKey) ? values : [...values, routeKey]); onUseDates(dates); }} /> :
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={2}>
          <Box><Typography component="h2" variant="h6">Find dates with data</Typography>
            <Typography variant="body2" color="text.secondary">Browse recording dates and measurement availability when you need them.</Typography></Box>
          <Button onClick={loadAvailability} disabled={historyLoading} sx={{ flexShrink: 0 }}>
            {historyLoading ? 'Checking availability…' : historyError ? 'Retry availability' : 'Browse availability'}
          </Button>
        </Stack>
        {historyLoading && <Typography role="status" variant="body2" sx={{ mt: 1 }}>Checking recording dates across this participant’s collection history…</Typography>}
        {historyError && <Alert severity="warning" sx={{ mt: 2 }}>Historical availability could not be loaded. You can retry or choose dates and click Visualize.</Alert>}
      </Paper>}
    {catalog.length ? <DeviceMetricPicker summary={null} catalog={catalog} value="" selectedValues={selected} onChange={toggleMetric} onSelectionChange={setSelected} onClear={() => setSelected([])} collapseKey={applied?.run} /> :
      <Alert severity="warning" sx={{ mb: 2 }}>Measurement choices could not be loaded. Click Refresh to retry.</Alert>}
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 3 }}>
      <Typography variant="h6" component="h2">Selected measurements{choices.length ? ` (${choices.length})` : ''}</Typography>
      {!choices.length && <Typography color="text.secondary" sx={{ my: 1 }}>Choose your dates above, then select one or more measurement cards. Charts load when you click Visualize.</Typography>}
      <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ my: 2 }}>{choices.map(({ metric, fieldPath }) => <Stack key={metric.routeKey} gap={1.5} sx={{ width: '100%', minWidth: 0 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} flexWrap="wrap" gap={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
        <Stack direction="row" alignItems="center" gap={.5} sx={{ minWidth: 0, maxWidth: '100%' }}>
          <Chip label={metricPresentation(metric).label} aria-label={`Remove ${metricPresentation(metric).label}`} onDelete={() => toggleMetric(metric.routeKey)} sx={{ minWidth: 0, maxWidth: '100%' }} />
          <DeviceMeasurementContext metric={metric} fieldPath={fieldPath} catalog={catalog} selected={selected}
            onAdd={(routeKey, relatedField) => {
              if (selected.includes(routeKey)) return;
              setSelected(values => values.includes(routeKey) ? values : [...values, routeKey]);
              if (relatedField) setFields(previous => ({ ...previous, [routeKey]: relatedField }));
            }} />
        </Stack>
        <MetricVerification metric={metric} />
        {metric.fields.length > 1 && <TextField select size="small"
          label={metric.dataType === 'exercise' ? 'Measurement within Workouts' : `${metricPresentation(metric).label} measurement`} value={fieldPath}
          onChange={event => setFields(previous => ({ ...previous, [metric.routeKey]: event.target.value }))} sx={{ minWidth: { sm: 290 }, maxWidth: '100%' }}>
          {metric.fields.map(field => { const display = fieldPresentation(metric, field); return <MenuItem key={field.path} value={field.path}>{display.label}{display.unit ? ` (${display.unit})` : ''}{display.unreviewed ? ' · not reviewed' : ''}</MenuItem>; })}
        </TextField>}
        </Stack>
      </Stack>)}</Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ sm: 'center' }}>
        <Button variant="contained" size="large" startIcon={<BarChartOutlined />} onClick={visualize}
          disabled={!choices.length || !validRange(range) || (loading && !changed)}>
          {loading && !changed ? 'Visualizing…' : 'Visualize'}
        </Button>
        {changed && <Typography role="status" variant="body2" color="text.secondary">Selections changed. Click Visualize to update the charts.</Typography>}
      </Stack>
    </Paper>
    {exportError && <Alert severity="error" sx={{ mb: 2 }}>{exportError}</Alert>}
    {applied && <>
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>Displayed charts: {applied.range.startDate} – {applied.range.endDate}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: applied.choices.length > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr' }, gap: 2 }}>
        {applied.choices.map(choice => {
          const result = results[choice.metric.routeKey];
          return <Box key={`${applied.run}:${choice.metric.routeKey}`} component="section" aria-label={`${metricPresentation(choice.metric).label} visualization`} sx={{ minWidth: 0 }}>
            {!result ? <Paper variant="outlined" sx={{ p: 4, borderRadius: 3 }}><CircularProgress size={24} aria-label={`Loading ${metricPresentation(choice.metric).label}`} /><Typography>{metricPresentation(choice.metric).label}</Typography></Paper> : result.failed ?
              <Alert severity="error">Could not load {metricPresentation(choice.metric).label}. Click Visualize to retry.</Alert> : choice.fieldPath ?
                <FitbitNumericChart metric={choice.metric} fieldPath={choice.fieldPath} points={result.points || []} dates={chartDates} /> :
                <FitbitStructuredChart metric={choice.metric} records={result.page?.records || []} hasMore={result.page?.nextOffset != null} dates={chartDates} />}
            {result && !result.failed && <Button size="small" startIcon={<FileDownloadOutlined />} sx={{ mt: 1 }} disabled={exporting !== null} onClick={() => download(choice.metric.routeKey)}>
              {exporting === choice.metric.routeKey ? 'Preparing export…' : `Export ${metricPresentation(choice.metric).label}`}
            </Button>}
          </Box>;
        })}
      </Box>
      {exporting && <Button onClick={() => exportController.current?.abort()}>Cancel export</Button>}
    </>}
  </>;
}
